import React from 'react';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend, Title);

const ScatterPlotResults = ({ results, questionnaireConfig }) => {
  if (!results || !results.scatterData) {
    return <div>Données de résultats non disponibles</div>;
  }

  const { scatterData, results: groupResults } = results;

  // Create chart data for each scatter plot pair
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        min: 1,
        max: 7,
        title: {
          display: true,
          text: 'Score X'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        }
      },
      y: {
        min: 1,
        max: 7,
        title: {
          display: true,
          text: 'Score Y'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        }
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `(${context.parsed.x.toFixed(2)}, ${context.parsed.y.toFixed(2)})`;
          }
        }
      }
    }
  };

  const getChartData = (scatterItem) => ({
    datasets: [
      {
        label: 'Votre position',
        data: [
          {
            x: scatterItem.xValue,
            y: scatterItem.yValue,
          },
        ],
        backgroundColor: questionnaireConfig.color,
        borderColor: questionnaireConfig.color,
        pointRadius: 10,
        pointHoverRadius: 12,
      },
      // Add reference lines
      {
        label: 'Ligne médiane X',
        data: [{ x: 4, y: 1 }, { x: 4, y: 7 }],
        borderColor: 'rgba(128, 128, 128, 0.5)',
        borderDash: [5, 5],
        pointRadius: 0,
        showLine: true,
      },
      {
        label: 'Ligne médiane Y',
        data: [{ x: 1, y: 4 }, { x: 7, y: 4 }],
        borderColor: 'rgba(128, 128, 128, 0.5)',
        borderDash: [5, 5],
        pointRadius: 0,
        showLine: true,
      },
      // Add diagonal reference line
      {
        label: 'Ligne diagonale de référence',
        data: [{ x: 1, y: 1 }, { x: 7, y: 7 }],
        borderColor: 'rgba(231, 76, 60, 0.8)',
        borderWidth: 2,
        pointRadius: 0,
        showLine: true,
      },
    ],
  });

  return (
    <div className="scatter-plot-results">
      {/* Summary Table */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
          Résumé de vos orientations de conscience
        </h3>
        <div className="table-responsive">
          <table className="results-table">
            <thead>
              <tr>
                <th>Dimension</th>
                <th>Score Moyen</th>
                <th>Interprétation</th>
              </tr>
            </thead>
            <tbody>
              {groupResults
                .sort((a, b) => b.average - a.average)
                .map((group) => (
                  <tr key={group.groupId}>
                    <td style={{ fontWeight: 'bold' }}>{group.name}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span
                        style={{
                          backgroundColor: 
                            group.average >= 5.5 ? '#27ae60' :
                            group.average >= 4.5 ? '#f39c12' : '#e74c3c',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                        }}
                      >
                        {group.average.toFixed(2)}
                      </span>
                    </td>
                    <td>
                      {group.average >= 5.5 && 'Orientation forte'}
                      {group.average >= 4.5 && group.average < 5.5 && 'Orientation modérée'}
                      {group.average < 4.5 && 'Orientation faible'}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Scatter Plots */}
      <div className="scatter-plots-container">
        <h3 style={{ color: '#2c3e50', marginBottom: '1.5rem', textAlign: 'center' }}>
          Graphiques de positionnement
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
          gap: '2rem',
          marginBottom: '2rem'
        }}>
          {scatterData.map((scatterItem) => (
            <div key={scatterItem.id} className="card" style={{ padding: '1.5rem' }}>
              <h4 style={{ 
                color: '#2c3e50', 
                marginBottom: '1rem', 
                textAlign: 'center',
                fontSize: '1rem'
              }}>
                {scatterItem.title}
              </h4>
              <div style={{ height: '300px', marginBottom: '1rem' }}>
                <Scatter
                  data={getChartData(scatterItem)}
                  options={{
                    ...chartOptions,
                    scales: {
                      ...chartOptions.scales,
                      x: {
                        ...chartOptions.scales.x,
                        title: {
                          display: true,
                          text: scatterItem.xLabel
                        }
                      },
                      y: {
                        ...chartOptions.scales.y,
                        title: {
                          display: true,
                          text: scatterItem.yLabel
                        }
                      }
                    }
                  }}
                />
              </div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '0.5rem',
                fontSize: '0.9rem',
                backgroundColor: '#f8f9fa',
                padding: '1rem',
                borderRadius: '4px'
              }}>
                <div>
                  <strong>{scatterItem.xLabel}:</strong> {scatterItem.xValue.toFixed(2)}
                </div>
                <div>
                  <strong>{scatterItem.yLabel}:</strong> {scatterItem.yValue.toFixed(2)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Interpretation Guide */}
      <div className="card" style={{ marginTop: '2rem' }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
          Guide d'interprétation
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '1rem'
        }}>
          <div style={{ 
            backgroundColor: '#e8f5e8', 
            padding: '1rem', 
            borderRadius: '8px',
            borderLeft: '4px solid #27ae60'
          }}>
            <h4 style={{ color: '#27ae60', margin: '0 0 0.5rem 0' }}>Score élevé (5.5-7)</h4>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              Cette dimension vous caractérise fortement et influence significativement vos comportements et décisions.
            </p>
          </div>
          <div style={{ 
            backgroundColor: '#fef9e7', 
            padding: '1rem', 
            borderRadius: '8px',
            borderLeft: '4px solid #f39c12'
          }}>
            <h4 style={{ color: '#f39c12', margin: '0 0 0.5rem 0' }}>Score modéré (4.5-5.4)</h4>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              Cette dimension vous influence de façon équilibrée, selon les contextes et situations.
            </p>
          </div>
          <div style={{ 
            backgroundColor: '#fdf2f2', 
            padding: '1rem', 
            borderRadius: '8px',
            borderLeft: '4px solid #e74c3c'
          }}>
            <h4 style={{ color: '#e74c3c', margin: '0 0 0.5rem 0' }}>Score faible (1-4.4)</h4>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              Cette dimension vous caractérise peu et influence moins vos comportements habituels.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScatterPlotResults;